import { useState, useEffect, useCallback } from "react";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui/radioGroupField";
import clsx from "clsx";
import { LOAN_PURPOSE_BUY_A_HOME, LOAN_PURPOSE_REFINANCE_MORTGAGE } from "@/lib/constants";
import { secureInput } from "@/lib/security";

export const MortgageLoanDetails = () => {
	const { formValues, setFormValue, clearFormError, setFormError } = useFormDataStore();
	const {
		selectedIndex,
		enabledFields,
		setFormErrors,
		setValidateFormDetails,
		formContainer,
		hasAttemptedSubmit,
		purchasePriceMin,
		depositAmountMin,
	} = useFormStateStore();

	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = selectedIndex === 0 ? 350 : 460;

		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	const {
		purchasePrice,
		depositAmount,
		firstHomeBuyer,
		propertyPurpose,
		fixedRate,
		interestRate,
		lenderName,
		propertyValue,
		currentMortgage,
	} = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		purchasePrice: false,
		depositAmount: false,
		firstHomeBuyer: false,
		propertyPurpose: false,
		fixedRate: false,
		interestRate: false,
		lenderName: false,
		propertyValue: false,
		currentMortgage: false,
	});

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const isBuying = formValues.loanPurpose === LOAN_PURPOSE_BUY_A_HOME;
		const isRefinancing = formValues.loanPurpose === LOAN_PURPOSE_REFINANCE_MORTGAGE;

		const newErrors = {
			// LOAN_PURPOSE_BUY_A_HOME
			purchasePrice: isBuying
				? !purchasePrice || parseFloat(purchasePrice.replace(/,/g, "")) < purchasePriceMin
				: false,
			depositAmount: isBuying
				? !depositAmount || parseFloat(depositAmount.replace(/,/g, "")) < depositAmountMin
				: false,
			firstHomeBuyer: isBuying ? !firstHomeBuyer || firstHomeBuyer === "" : false,
			propertyPurpose: isBuying ? !propertyPurpose || propertyPurpose === "" : false,

			// LOAN_PURPOSE_REFINANCE_MORTGAGE
			fixedRate: isRefinancing
				? interestRate && interestRate !== "" && !fixedRate
					? true
					: false
				: false,
			interestRate: isRefinancing
				? interestRate !== "" && isNaN(parseFloat(interestRate.replace(/,/g, "")))
				: false,
			lenderName: isRefinancing ? !secureInput.lenderName(lenderName).isValid : false,
			propertyValue: isRefinancing
				? !propertyValue || isNaN(parseFloat(propertyValue.replace(/,/g, "")))
				: false,
			currentMortgage: isRefinancing
				? !currentMortgage || isNaN(parseFloat(currentMortgage.replace(/,/g, "")))
				: false,
		};

		setErrors(newErrors);

		const hasError = Object.values(newErrors).some(Boolean);
		setFormErrors(hasError);

		return !hasError;
	};
	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [
		purchasePrice,
		depositAmount,
		firstHomeBuyer,
		propertyPurpose,
		fixedRate,
		interestRate,
		lenderName,
		propertyValue,
		currentMortgage,
	]);

	// Reset formValues where it's not enabled.
	useEffect(() => {
		const fieldsToReset: (keyof typeof formValues)[] = [
			"purchasePrice",
			"depositAmount",
			"firstHomeBuyer",
			"propertyPurpose",
			"fixedRate",
			"interestRate",
			"propertyValue",
			"currentMortgage",
		];

		fieldsToReset.forEach((field) => {
			if (enabledFields[field]?.length === 0) {
				setFormValue(field, ""); // Reset value when no options are available
				setErrors((prevErrors) => ({
					...prevErrors,
					field: false,
				}));
			}
		});
	}, [enabledFields, setFormValue, setErrors]);

	// Reset form errors on focus
	const handleFocus = (field: keyof typeof errors) => {
		setErrors((prev) => ({ ...prev, [field]: false }));
	};

	const handlePurchasePriceChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
			setFormValue("purchasePrice", formattedValue.toLocaleString());
			setErrors((prev) => ({
				...prev,
				purchasePrice: formattedValue < purchasePriceMin,
			}));
		},
		[setFormValue, setFormError, setErrors],
	);

	const handleDepositAmountChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
			setFormValue("depositAmount", formattedValue.toLocaleString());
			setErrors((prev) => ({
				...prev,
				depositAmount: formattedValue < depositAmountMin,
			}));
		},
		[setFormValue, setFormError, setErrors],
	);

	const handleFirstHomeBuyerChange = useCallback(
		(value: string) => {
			setFormValue("firstHomeBuyer", value);
			setErrors((prevErrors) => ({
				...prevErrors,
				firstHomeBuyer: !value, // Clear error when a selection is made
			}));
		},
		[setFormValue, setFormError, setErrors, clearFormError],
	);

	const handlePropertyPurposeChange = useCallback(
		(value: string) => {
			setFormValue("propertyPurpose", value);
			setErrors((prevErrors) => ({
				...prevErrors,
				propertyPurpose: !value, // Clear error when a selection is made
			}));
		},
		[setFormValue, setFormError, setErrors, clearFormError],
	);

	const handleInterestRateChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.rate(value);
			setFormValue("interestRate", sanitizedValue.sanitized);
			setErrors((prevErrors) => ({
				...prevErrors,
				interestRate: !sanitizedValue.isValid,
			}));
		},
		[setFormValue, setFormError, setErrors],
	);

	const handleFixedRateChange = useCallback(
		(value: string) => {
			setFormValue("fixedRate", value);
			setErrors((prevErrors) => ({
				...prevErrors,
				fixedRate: !value,
			}));
		},
		[setFormValue, setFormError, setErrors, clearFormError],
	);

	const handleLenderNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.lenderName(value);
			setFormValue("lenderName", sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, lenderName: !sanitizedValue.isValid }));
		},
		[setFormValue, setFormError, setErrors],
	);

	const handlePropertyValueChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
			setFormValue("propertyValue", formattedValue.toLocaleString());
			setErrors((prev) => ({
				...prev,
			}));
		},
		[setFormValue, setFormError, setErrors],
	);

	const handleCurrentMortgageChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
			setFormValue("currentMortgage", formattedValue.toLocaleString());
			setErrors((prev) => ({
				...prev,
			}));
		},
		[setFormValue, setFormError, setErrors],
	);

	if (formValues.loanPurpose === LOAN_PURPOSE_BUY_A_HOME) {
		// Buy a Home
		return (
			<div className="yF-space-y-4 yF-m-2 yF-pb-4">
				<div
					className={clsx(
						isNavDivSmall
							? "yF-flex-col yF-gap-x-2  yF-space-y-2"
							: "yF-flex-row yF-gap-x-2",
						"yF-flex",
					)}
				>
					{/* Purchase Price */}
					<div className="yF-flex-1">
						<label
							htmlFor="purchase-price"
							className="yF-text-base yF-font-semibold yF-leading-6 yF-text-gray-900"
						>
							What is the estimated purchase price?
						</label>
						<div className="yF-relative yF-mt-2">
							<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-left-0 yF-flex yF-items-center yF-pl-3">
								<span className="yF-text-gray-500 sm:yF-text-md">$</span>
							</div>
							<input
								value={purchasePrice}
								onChange={handlePurchasePriceChange}
								onFocus={() => handleFocus("purchasePrice")}
								aria-label="Purchase Price"
								placeholder={"Purchase Price (Min 50K)"}
								maxLength={10} /** Max length of 10 digits including commas */
								className={clsx(
									hasAttemptedSubmit && errors.purchasePrice
										? "yF-ring-red-500 focus:yF-ring-red-500"
										: "yF-ring-gray-300 focus:yF-ring-[--yF-theme-color]",
									"yF-block yF-w-full yF-rounded-md yF-shadow-sm yF-border-0 yF-py-2.5 yF-pl-7 yF-pr-12 yF-text-gray-900 yF-ring-1 yF-ring-gray-300 focus:yF-ring-1 yF-outline-1 focus:yF-ring-inset focus-visible:yF-outline-[--yF-theme-color] placeholder:yF-text-gray-400 sm:yF-text-md sm:yF-leading-6",
								)}
								type="text"
								id="purchase-price"
								aria-describedby="purchase-price"
								inputMode="numeric"
							/>
							<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-right-0 yF-flex yF-items-center yF-pr-3">
								<span className="yF-text-gray-500 sm:yF-text-md">AUD</span>
							</div>
						</div>
						{hasAttemptedSubmit && errors.purchasePrice && (
							<span className="yF-text-xs yF-text-red-500">
								Minimum accepted purchase price is $
								{purchasePriceMin.toLocaleString()}
							</span>
						)}
					</div>
				</div>

				{/* Deposit */}
				<div className="yF-flex-1">
					<label
						htmlFor="deposit-amount"
						className="yF-text-base yF-font-semibold yF-leading-6 yF-text-gray-900"
					>
						How much deposit do you have?
					</label>
					<div className="yF-relative yF-mt-2">
						<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-left-0 yF-flex yF-items-center yF-pl-3">
							<span className="yF-text-gray-500 sm:yF-text-md">$</span>
						</div>
						<input
							value={depositAmount}
							onChange={handleDepositAmountChange}
							onFocus={() => handleFocus("depositAmount")}
							aria-label="Deposit Amount"
							placeholder={"Deposit Amount (Min 2.5K)"}
							maxLength={10} /** Max length of 10 digits including commas */
							className={clsx(
								hasAttemptedSubmit && errors.depositAmount
									? "yF-ring-red-500 focus:yF-ring-red-500"
									: "yF-ring-gray-300 focus:yF-ring-[--yF-theme-color]",
								"yF-block yF-w-full yF-rounded-md yF-shadow-sm yF-border-0 yF-py-2.5 yF-pl-7 yF-pr-12 yF-text-gray-900 yF-ring-1 yF-ring-gray-300 focus:yF-ring-1 yF-outline-1 focus:yF-ring-inset focus-visible:yF-outline-[--yF-theme-color] placeholder:yF-text-gray-400 sm:yF-text-md sm:yF-leading-6",
							)}
							type="text"
							id="deposit-amount"
							aria-describedby="deposit-amount"
							inputMode="numeric"
						/>
						<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-right-0 yF-flex yF-items-center yF-pr-3">
							<span className="yF-text-gray-500 sm:yF-text-md">AUD</span>
						</div>
					</div>
					{hasAttemptedSubmit && errors.depositAmount && (
						<span className="yF-text-xs yF-text-red-500">
							Minimum accepted deposit amount is ${depositAmountMin.toLocaleString()}
						</span>
					)}
				</div>

				{/* FHB */}
				{enabledFields.firstHomeBuyer?.length != 0 && (
					<RadioGroupField
						id="firstHomeBuyer"
						label="Are you a first home buyer?"
						value={firstHomeBuyer || ""}
						options={enabledFields.firstHomeBuyer.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
						}))}
						onChange={(value) => {
							handleFirstHomeBuyerChange(value);
						}}
						required={true}
						error={errors.firstHomeBuyer ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={2}
						showIcons={false}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				)}

				{/* Property Purpose */}
				{enabledFields.propertyPurpose?.length != 0 && (
					<RadioGroupField
						id="propertyPurpose"
						label="What will be the property purpose?"
						value={propertyPurpose || ""}
						options={enabledFields.propertyPurpose.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
						}))}
						onChange={(value) => {
							handlePropertyPurposeChange(value);
						}}
						required={true}
						error={errors.propertyPurpose ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={2}
						showIcons={false}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				)}
			</div>
		);
	} else {
		// Refinancing Mortgage
		return (
			<div className="yF-space-y-4 yF-m-2 yF-pb-4">
				<div
					className={clsx(
						isNavDivSmall
							? "yF-flex-col yF-gap-x-2  yF-space-y-2"
							: "yF-flex-row yF-gap-x-2",
						"yF-flex",
					)}
				>
					{/* Interest Rate */}
					<div className="yF-flex-1">
						<label
							htmlFor="interest-rate"
							className="yF-text-base yF-font-semibold yF-leading-6 yF-text-gray-900"
						>
							What is your current interest rate?
						</label>
						<div className="yF-relative yF-mt-2">
							<input
								value={interestRate}
								onChange={handleInterestRateChange}
								onFocus={() => handleFocus("interestRate")}
								inputMode="decimal"
								step="0.01"
								aria-label="Interest Rate"
								placeholder={"Interest rate (Optional)"}
								maxLength={10} /** Max length of 10 digits including commas */
								className={clsx(
									hasAttemptedSubmit && errors.interestRate
										? "yF-ring-red-500 focus:yF-ring-red-500"
										: "yF-ring-gray-300 focus:yF-ring-[--yF-theme-color]",
									"yF-block yF-w-full yF-rounded-md yF-shadow-sm yF-border-0 yF-py-2.5 yF-pl-3 yF-pr-16 yF-text-gray-900 yF-ring-1 yF-ring-gray-300 focus:yF-ring-1 yF-outline-1 focus:yF-ring-inset focus-visible:yF-outline-[--yF-theme-color] placeholder:yF-text-gray-400 sm:yF-text-md sm:yF-leading-6",
								)}
								type="number"
								id="interest-rate"
								aria-describedby="interest-rate"
							/>
							<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-right-0 yF-flex yF-items-center yF-pr-3">
								<span className="yF-text-gray-500 sm:yF-text-md">% p.a.</span>
							</div>
						</div>
						{hasAttemptedSubmit && errors.interestRate && (
							<span className="yF-text-xs yF-text-red-500">
								Please enter a valid rate
							</span>
						)}
					</div>

					{/* Fixed Rate */}
					{enabledFields.fixedRate?.length != 0 &&
						interestRate &&
						interestRate !== "" && (
							<RadioGroupField
								id="fixedRate"
								label="Are you on a fixed rate?"
								value={fixedRate || ""}
								options={enabledFields.fixedRate.map((item) => ({
									id: item.id,
									value: item.value,
									label: item.value,
								}))}
								onChange={(value) => {
									handleFixedRateChange(value);
								}}
								required={true}
								error={errors.fixedRate ? "This field is required" : ""}
								disabled={false}
								className={""}
								gridCols={2}
								showIcons={false}
								disabledOptions={[]}
								navRef={isNavDivSmall}
							/>
						)}
				</div>

				{/* Lender Name */}
				<div className="yF-flex-1">
					<label
						htmlFor="lender-name"
						className="yF-text-base yF-font-semibold yF-leading-6 yF-text-gray-900"
					>
						Who is your current lender?
					</label>
					<div className="yF-relative yF-mt-2">
						<input
							name="lenderName"
							value={lenderName}
							onChange={handleLenderNameChange}
							onFocus={() => handleFocus("lenderName")}
							aria-label="Lender Name"
							type="text"
							id="lenderName"
							placeholder="Lender Name"
							maxLength={30}
							className={clsx(
								hasAttemptedSubmit && errors.lenderName
									? "yF-ring-red-500 focus:yF-ring-red-500"
									: "yF-ring-gray-300 focus:yF-ring-[--yF-theme-color]",
								"yF-block yF-w-full yF-rounded-md yF-shadow-sm yF-border-0 yF-py-2.5 yF-text-gray-900 yF-ring-1 yF-outline-1 yF-outline-gray-900 focus:yF-ring-1 focus:yF-ring-inset focus-visible:yF-outline-[--yF-theme-color] yF-pl-3 placeholder:yF-text-gray-400 sm:yF-text-md sm:yF-leading-6",
							)}
						/>
					</div>
					{hasAttemptedSubmit && errors.lenderName && (
						<span className="yF-text-xs yF-text-red-500">Lender name required</span>
					)}
				</div>

				{/* Property Value */}
				<div className="yF-flex-1">
					<label
						htmlFor="property-value"
						className="yF-text-base yF-font-semibold yF-leading-6 yF-text-gray-900"
					>
						What is the estimated property value?
					</label>
					<div className="yF-relative yF-mt-2">
						<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-left-0 yF-flex yF-items-center yF-pl-3">
							<span className="yF-text-gray-500 sm:yF-text-md">$</span>
						</div>
						<input
							value={propertyValue}
							onChange={handlePropertyValueChange}
							onFocus={() => handleFocus("propertyValue")}
							aria-label="Property Value"
							placeholder={"Estimated property value"}
							maxLength={10} /** Max length of 10 digits including commas */
							className={clsx(
								hasAttemptedSubmit && errors.propertyValue
									? "yF-ring-red-500 focus:yF-ring-red-500"
									: "yF-ring-gray-300 focus:yF-ring-[--yF-theme-color]",
								"yF-block yF-w-full yF-rounded-md yF-shadow-sm yF-border-0 yF-py-2.5 yF-pl-7 yF-pr-12 yF-text-gray-900 yF-ring-1 yF-ring-gray-300 focus:yF-ring-1 yF-outline-1 focus:yF-ring-inset focus-visible:yF-outline-[--yF-theme-color] placeholder:yF-text-gray-400 sm:yF-text-md sm:yF-leading-6",
							)}
							type="text"
							id="property-value"
							aria-describedby="property-value"
							inputMode="numeric"
						/>
						<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-right-0 yF-flex yF-items-center yF-pr-3">
							<span className="yF-text-gray-500 sm:yF-text-md">AUD</span>
						</div>
					</div>
					{hasAttemptedSubmit && errors.propertyValue && (
						<span className="yF-text-xs yF-text-red-500">
							Please enter a valid number
						</span>
					)}
				</div>

				{/* Current Mortgage */}
				<div className="yF-flex-1">
					<label
						htmlFor="current-mortgage"
						className="yF-text-base yF-font-semibold yF-leading-6 yF-text-gray-900"
					>
						How much is left on your current mortgage?
					</label>
					<div className="yF-relative yF-mt-2">
						<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-left-0 yF-flex yF-items-center yF-pl-3">
							<span className="yF-text-gray-500 sm:yF-text-md">$</span>
						</div>
						<input
							value={currentMortgage}
							onChange={handleCurrentMortgageChange}
							onFocus={() => handleFocus("currentMortgage")}
							aria-label="Current Mortgage"
							placeholder={"Current mortgage owed"}
							maxLength={10} /** Max length of 10 digits including commas */
							className={clsx(
								hasAttemptedSubmit && errors.currentMortgage
									? "yF-ring-red-500 focus:yF-ring-red-500"
									: "yF-ring-gray-300 focus:yF-ring-[--yF-theme-color]",
								"yF-block yF-w-full yF-rounded-md yF-shadow-sm yF-border-0 yF-py-2.5 yF-pl-7 yF-pr-12 yF-text-gray-900 yF-ring-1 yF-ring-gray-300 focus:yF-ring-1 yF-outline-1 focus:yF-ring-inset focus-visible:yF-outline-[--yF-theme-color] placeholder:yF-text-gray-400 sm:yF-text-md sm:yF-leading-6",
							)}
							type="text"
							id="current-mortgage"
							aria-describedby="current-mortgage"
							inputMode="numeric"
						/>
						<div className="yF-pointer-events-none yF-absolute yF-inset-y-0 yF-right-0 yF-flex yF-items-center yF-pr-3">
							<span className="yF-text-gray-500 sm:yF-text-md">AUD</span>
						</div>
					</div>
					{hasAttemptedSubmit && errors.currentMortgage && (
						<span className="yF-text-xs yF-text-red-500">
							Please enter a valid number
						</span>
					)}
				</div>
			</div>
		);
	}
};
